// src/components/AiDashboard/AiMyNutritionStatus/AiUser/AiUserInputSeparator/AiMealPortionAdjuster.js
// Turn AI "consumed" items into adjustable portions and recompute nutrients/label
// when the user clicks the +/- controls. This module also builds an adjusted
// JSON snapshot where "consumed", "consolidated_nutrients", "labeling", and "antioxidants"
// all reflect the current UI multipliers.

const STEP = 0.25;          // 1/4 step per click
const MIN_SCALE = STEP;     // do not go below 1/4 of base portion

// Default daily value references for antioxidant nutrients
const DEFAULT_DAILY_VALUES = {
  vitaminA_µg_RE: 800,
  vitaminC_mg: 80,
  vitaminE_mg_aTE: 12,
  zinc_mg: 10,
  selenium_µg: 55,
  manganese_mg: 3,
};

// Heuristic thresholds for classifying very low-energy drinks
const DRINK_ENERGY_DENSITY_THRESHOLD_KCAL_PER_100G = 10;
const DRINK_MIN_MASS_FOR_DENSITY_RULE_GRAMS = 50;

export function createAdjustableMealFromNutrition(latestNutritionJson) {
  const consumed = Array.isArray(latestNutritionJson?.consumed)
    ? latestNutritionJson.consumed
    : [];

  const items = consumed.map((item, index) => {
    const portion = isPlainObject(item?.portion) ? item.portion : {};
    const baseQuantity = toFiniteNumber(portion.quantity, 1);
    const baseGramEquivalent = toFiniteNumber(
      portion.gramEquivalent,
      baseQuantity // default: 1 g per unit-ish if missing
    );
    const unit =
      typeof portion.unit === 'string' && portion.unit.trim()
        ? portion.unit.trim()
        : 'unit';

    const baseNutrients = isPlainObject(item?.nutrients)
      ? { ...item.nutrients }
      : {};

    return {
      id:
        typeof item?.id === 'string' && item.id.trim()
          ? item.id
          : `item_${index + 1}`,
      label:
        typeof item?.label === 'string' && item.label.trim()
          ? toTitleCase(item.label.trim())
          : `Item ${index + 1}`,
      basePortion: {
        quantity: baseQuantity,
        unit,
        gramEquivalent: baseGramEquivalent,
        isEstimated:
          typeof portion.isEstimated === 'boolean'
            ? portion.isEstimated
            : true,
      },
      baseNutrients,
      scale: 1, // 1.0 = as estimated by AI
    };
  });

  const { totals, labeling } = recomputeTotalsAndLabeling(items);

  return { items, totals, labeling };
}

/**
 * Increase a single item's portion by 1/4 of its base portion.
 */
export function incrementItemPortion(mealState, itemId) {
  return updateItemScale(mealState, itemId, STEP);
}

/**
 * Decrease a single item's portion by 1/4 of its base portion.
 * Never goes below MIN_SCALE (e.g., 1/4 of base).
 */
export function decrementItemPortion(mealState, itemId) {
  return updateItemScale(mealState, itemId, -STEP);
}

/**
 * Format the portion line under the title:
 * e.g. "1 3/4 unit (estimated)" or "100 g".
 */
export function getDisplayPortion(item) {
  const scale = getScale(item);
  const quantity = item.basePortion.quantity * scale;
  const unit = item.basePortion.unit;
  const isEstimated = !!item.basePortion.isEstimated;

  const quantityString = formatQuarterQuantity(quantity);
  const estimateSuffix = isEstimated ? ' (estimated)' : '';

  return `${quantityString} ${unit}${estimateSuffix}`;
}

/**
 * Format the central quantity (inside <span class="aiu-qty">).
 * This is the multiplier relative to the AI-estimated portion.
 * e.g. "1", "0.75", "1.25", "1.5".
 */
export function getDisplayQtyMultiplier(item) {
  const scale = getScale(item);
  return formatDecimal(scale);
}

/**
 * Take the original AI JSON + current adjustable meal state and produce
 * a new JSON object with:
 *   - updated "consumed" (scaled portions and nutrients),
 *   - updated "consolidated_nutrients",
 *   - updated "labeling",
 *   - updated "antioxidants" (dynamically calculated).
 *
 * This adjusted JSON can then be passed into:
 *   createNutritionFactsFromLastNutrition(adjustedJson)
 * so that the Nutrition Facts label and the JSON both reflect the new portions.
 */
export function applyAdjustmentsToNutritionJson(baseJson, mealState) {
  const safeBase = isPlainObject(baseJson) ? baseJson : {};
  const items = Array.isArray(mealState?.items) ? mealState.items : [];

  // Build a consumed[] array that reflects the current UI scales.
  const adjustedConsumed = buildAdjustedConsumedItems(safeBase, mealState);

  // Recompute consolidated totals and labeling from the internal item state.
  const { totals, labeling } = recomputeTotalsAndLabeling(items);

  // Calculate antioxidants from the adjusted consolidated nutrients
  const dailyValueRef = safeBase.dailyValueReferenceSnapshot || null;
  const antioxidants = calculateAntioxidants(totals, dailyValueRef);

  return {
    ...safeBase,
    consumed: adjustedConsumed,
    consolidated_nutrients: {
      ...(safeBase.consolidated_nutrients || {}),
      ...totals,
    },
    labeling: {
      ...(safeBase.labeling || {}),
      ...labeling,
    },
    antioxidants,
  };
}

// ---------- Antioxidant Calculation ----------

/**
 * Calculate antioxidant score and details from consolidated nutrients
 * @param {Object} consolidatedNutrients - The meal's total nutrients
 * @param {Object} dailyValueReference - Optional custom daily value reference
 * @returns {Object} Antioxidant data with score, level, label, and per-nutrient percentages
 */
function calculateAntioxidants(consolidatedNutrients, dailyValueReference = null) {
  const dvRef = dailyValueReference || DEFAULT_DAILY_VALUES;

  const vitaminA = toFiniteNumber(consolidatedNutrients?.vitaminA_µg_RE, 0);
  const vitaminC = toFiniteNumber(consolidatedNutrients?.vitaminC_mg, 0);
  const vitaminE = toFiniteNumber(consolidatedNutrients?.vitaminE_mg_aTE, 0);
  const zinc = toFiniteNumber(consolidatedNutrients?.zinc_mg, 0);
  const selenium = toFiniteNumber(consolidatedNutrients?.selenium_µg, 0);
  const manganese = toFiniteNumber(consolidatedNutrients?.manganese_mg, 0);

  const perNutrientPct = {
    vitaminA_µg_RE: Math.round((vitaminA / dvRef.vitaminA_µg_RE) * 100),
    vitaminC_mg: Math.round((vitaminC / dvRef.vitaminC_mg) * 100),
    vitaminE_mg_aTE: Math.round((vitaminE / dvRef.vitaminE_mg_aTE) * 100),
    zinc_mg: Math.round((zinc / dvRef.zinc_mg) * 100),
    selenium_µg: Math.round((selenium / dvRef.selenium_µg) * 100),
    manganese_mg: Math.round((manganese / dvRef.manganese_mg) * 100),
  };

  const scores = [
    perNutrientPct.vitaminA_µg_RE,
    perNutrientPct.vitaminC_mg,
    perNutrientPct.vitaminE_mg_aTE,
    perNutrientPct.zinc_mg,
    perNutrientPct.selenium_µg,
    perNutrientPct.manganese_mg,
  ];

  const score0to100 = Math.round(
    scores.reduce((sum, score) => sum + score, 0) / scores.length
  );

  let level = 'low';
  if (score0to100 >= 60) {
    level = 'high';
  } else if (score0to100 >= 35) {
    level = 'medium';
  }

  const label = `Antioxidants: ${capitalizeFirstLetter(level)} (${score0to100}%)`;

  return {
    label,
    level,
    perNutrientPct,
    score0to100,
  };
}

/**
 * Helper: capitalize first letter
 */
function capitalizeFirstLetter(str) {
  if (typeof str !== 'string' || !str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// ---------- Internals ----------

function updateItemScale(mealState, itemId, deltaScale) {
  const previousItems = Array.isArray(mealState?.items) ? mealState.items : [];
  const nextItems = previousItems.map((item) => {
    if (item.id !== itemId) return item;

    const currentScale = getScale(item);
    let nextScale = currentScale + deltaScale;

    if (nextScale < MIN_SCALE) nextScale = MIN_SCALE;

    return {
      ...item,
      scale: nextScale,
    };
  });

  const { totals, labeling } = recomputeTotalsAndLabeling(nextItems);

  return {
    ...mealState,
    items: nextItems,
    totals,
    labeling,
  };
}

/**
 * Safely read a numeric scale with sane defaults.
 */
function getScale(item) {
  const candidate = item && typeof item.scale === 'number' ? item.scale : 1;
  if (!Number.isFinite(candidate) || candidate <= 0) return 1;
  return candidate;
}

/**
 * Build an adjusted consumed[] array based on the current mealState scales.
 * Portion.quantity, portion.gramEquivalent and all numeric nutrient values
 * are scaled by the item's multiplier.
 */
function buildAdjustedConsumedItems(baseJson, mealState) {
  const safeBase = isPlainObject(baseJson) ? baseJson : {};
  const baseConsumed = Array.isArray(safeBase.consumed)
    ? safeBase.consumed
    : [];
  const items = Array.isArray(mealState?.items) ? mealState.items : [];

  if (!baseConsumed.length || !items.length) {
    return baseConsumed;
  }

  const scaleById = new Map();
  for (const item of items) {
    if (!item || !item.id) continue;
    scaleById.set(item.id, getScale(item));
  }

  return baseConsumed.map((consumedItem) => {
    if (!isPlainObject(consumedItem)) {
      return consumedItem;
    }

    const scale = scaleById.has(consumedItem.id)
      ? scaleById.get(consumedItem.id)
      : 1;

    if (!isFiniteNumber(scale) || almostEquals(scale, 1)) {
      return { ...consumedItem };
    }

    const basePortion = isPlainObject(consumedItem.portion)
      ? consumedItem.portion
      : {};
    const baseNutrients = isPlainObject(consumedItem.nutrients)
      ? consumedItem.nutrients
      : {};

    const scaledPortion = {
      ...basePortion,
    };

    if (isFiniteNumber(basePortion.quantity)) {
      scaledPortion.quantity = roundTo(
        basePortion.quantity * scale,
        3
      );
    }

    if (isFiniteNumber(basePortion.gramEquivalent)) {
      scaledPortion.gramEquivalent = roundTo(
        basePortion.gramEquivalent * scale,
        3
      );
    }

    const scaledNutrients = {};
    for (const [key, value] of Object.entries(baseNutrients)) {
      if (isFiniteNumber(value)) {
        scaledNutrients[key] = roundTo(value * scale, 3);
      } else {
        scaledNutrients[key] = value;
      }
    }

    return {
      ...consumedItem,
      portion: scaledPortion,
      nutrients: scaledNutrients,
    };
  });
}

// Recompute totals + label info for the current adjusted items.
function recomputeTotalsAndLabeling(items) {
  const safeItems = Array.isArray(items) ? items : [];

  const nutrientKeySet = new Set();
  for (const item of safeItems) {
    const nutrients = isPlainObject(item?.baseNutrients)
      ? item.baseNutrients
      : {};
    for (const key of Object.keys(nutrients)) {
      nutrientKeySet.add(key);
    }
  }
  const nutrientKeys = Array.from(nutrientKeySet);

  const totals = {};
  for (const key of nutrientKeys) {
    totals[key] = 0;
  }

  for (const item of safeItems) {
    const scale = getScale(item);
    const nutrients = isPlainObject(item?.baseNutrients)
      ? item.baseNutrients
      : {};
    for (const key of nutrientKeys) {
      const value = nutrients[key];
      if (isFiniteNumber(value)) {
        totals[key] += value * scale;
      }
    }
  }

  const labeling = buildDynamicLabelingSummary(
    totals,
    safeItems,
    nutrientKeys
  );
  return { totals, labeling };
}

// Label math that respects adjusted grams + energy.
// per100g_meal: all items (food + drinks)
// per100g_food and per100g: solids-only, excluding likely drinks (e.g. black coffee)
function buildDynamicLabelingSummary(consolidatedTotals, items, nutrientKeys) {
  const allItems = Array.isArray(items) ? items : [];

  const foodItems = allItems.filter(
    (item) => !isLikelyDrinkItemForLabeling(item)
  );

  const gramsAll = allItems.reduce((sum, item) => {
    const baseGrams = item?.basePortion?.gramEquivalent;
    const scale = getScale(item);
    if (!isFiniteNumber(baseGrams)) return sum;
    return sum + baseGrams * scale;
  }, 0);

  const gramsFood = foodItems.reduce((sum, item) => {
    const baseGrams = item?.basePortion?.gramEquivalent;
    const scale = getScale(item);
    if (!isFiniteNumber(baseGrams)) return sum;
    return sum + baseGrams * scale;
  }, 0);

  const per100gMeal = {};
  const per100gFood = {};
  const per100g = {};

  // Food-only totals (solids) for per100g_food
  const foodTotals = {};
  for (const key of nutrientKeys) {
    foodTotals[key] = 0;
  }

  for (const item of foodItems) {
    const scale = getScale(item);
    const nutrients = isPlainObject(item?.baseNutrients)
      ? item.baseNutrients
      : {};
    for (const key of nutrientKeys) {
      const value = nutrients[key];
      if (isFiniteNumber(value)) {
        foodTotals[key] += value * scale;
      }
    }
  }

  if (gramsAll > 0) {
    const factorMeal = 100 / gramsAll;
    for (const key of nutrientKeys) {
      const value = isFiniteNumber(consolidatedTotals[key])
        ? consolidatedTotals[key]
        : 0;
      per100gMeal[key] = roundTo(value * factorMeal, 3);
    }
  } else {
    for (const key of nutrientKeys) {
      per100gMeal[key] = 0;
    }
  }

  if (gramsFood > 0) {
    const factorFood = 100 / gramsFood;
    for (const key of nutrientKeys) {
      const value = isFiniteNumber(foodTotals[key]) ? foodTotals[key] : 0;
      const val = roundTo(value * factorFood, 3);
      per100gFood[key] = val;
      per100g[key] = val;
    }
  } else if (gramsAll > 0) {
    // Fallback: if everything is classified as drink, use whole-meal basis
    for (const key of nutrientKeys) {
      per100gFood[key] = per100gMeal[key];
      per100g[key] = per100gMeal[key];
    }
  } else {
    for (const key of nutrientKeys) {
      per100gFood[key] = 0;
      per100g[key] = 0;
    }
  }

  const effectiveFoodGrams =
    gramsFood > 0 ? gramsFood : gramsAll > 0 ? gramsAll : 0;

  return {
    portionGramEquivalent: roundTo(gramsAll, 3),
    portionGramEquivalentFood: roundTo(effectiveFoodGrams, 3),
    per100g,
    per100g_meal: per100gMeal,
    per100g_food: per100gFood,
  };
}

// Classify very low-energy drinks so they do not distort per-100 g food metrics.
function isLikelyDrinkItemForLabeling(item) {
  if (!item || typeof item !== 'object') {
    return false;
  }

  if (item.isDrink === true) {
    return true;
  }

  const labelText = String(item.label || '').toLowerCase();

  const drinkKeywords = [
    'water',
    'sparkling water',
    'mineral water',
    'coffee',
    'black coffee',
    'tea',
    'green tea',
    'herbal tea',
    'juice',
    'smoothie',
    'soda',
    'cola',
    'lemonade',
    'milk',
    'latte',
    'espresso',
    'cappuccino',
    'americano',
    'broth',
    'stock',
    'wine',
    'beer',
    'cider',
  ];

  if (drinkKeywords.some((keyword) => labelText.includes(keyword))) {
    return true;
  }

  const basePortion = item.basePortion || {};
  const unitText = String(basePortion.unit || '').toLowerCase();
  const grams = isFiniteNumber(basePortion.gramEquivalent)
    ? basePortion.gramEquivalent
    : 0;

  const baseNutrients = item.baseNutrients || {};
  const energyKcal = isFiniteNumber(baseNutrients.energy_kcal)
    ? baseNutrients.energy_kcal
    : 0;

  const volumeLikeUnits = [
    'cup',
    'cups',
    'glass',
    'glasses',
    'mug',
    'ml',
    'milliliter',
    'milliliters',
    'l',
    'liter',
    'liters',
  ];

  const isVolumeLikeUnit = volumeLikeUnits.includes(unitText);

  let energyDensityKcalPer100g = 0;
  if (grams > 0) {
    energyDensityKcalPer100g =
      (energyKcal / grams) * 100;
  }

  const isVeryLowEnergyDensity =
    grams > 0 &&
    energyDensityKcalPer100g <=
      DRINK_ENERGY_DENSITY_THRESHOLD_KCAL_PER_100G;

  if (isVolumeLikeUnit && isVeryLowEnergyDensity) {
    return true;
  }

  if (
    isVeryLowEnergyDensity &&
    grams >= DRINK_MIN_MASS_FOR_DENSITY_RULE_GRAMS
  ) {
    return true;
  }

  return false;
}

// ---------- Formatting helpers (quarters → "1 3/4", etc.) ----------

function formatQuarterQuantity(quantity) {
  if (!isFiniteNumber(quantity)) return '0';

  const snapped = Math.round(quantity * 4) / 4;

  const whole = Math.floor(snapped);
  const fraction = snapped - whole;

  let fractionString = '';
  if (almostEquals(fraction, 0)) {
    fractionString = '';
  } else if (almostEquals(fraction, 0.25)) {
    fractionString = '1/4';
  } else if (almostEquals(fraction, 0.5)) {
    fractionString = '1/2';
  } else if (almostEquals(fraction, 0.75)) {
    fractionString = '3/4';
  } else {
    return formatDecimal(snapped);
  }

  if (whole === 0 && fractionString) return fractionString;
  if (!fractionString) return String(whole);
  return `${whole} ${fractionString}`;
}

function formatDecimal(value) {
  if (!isFiniteNumber(value)) return '0';
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return String(rounded.toFixed(2))
    .replace(/0+$/, '')
    .replace(/\.$/, '');
}

function almostEquals(a, b, epsilon = 1e-6) {
  return Math.abs(a - b) < epsilon;
}

// ---------- Generic utilities ----------

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function roundTo(value, places = 3) {
  if (!isFiniteNumber(value)) return 0;
  const factor = Math.pow(10, places);
  return Math.round(value * factor) / factor;
}

function toFiniteNumber(value, fallback = 0) {
  const numeric =
    typeof value === 'number' ? value : Number(value ?? Number.NaN);
  if (!Number.isFinite(numeric)) return fallback;
  return numeric;
}

function toTitleCase(text) {
  if (typeof text !== 'string') return '';
  return text
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
